/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * OrderAccept (v1_577): the firm signatory's one screen at /order?token=...
 * The CompliantID -> firm Order Form, behind the OTP-1 gate in scope 'order'.
 * Mirrors TermsAccept.jsx so the two acceptance experiences are identical.
 */

import React, { useEffect, useState } from "react";

const PAGE = { minHeight: "100vh", background: "#f3f4f6", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px" };
const CARD = { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "36px 40px", width: 680, maxWidth: "96%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" };
const BTN = { width: "100%", padding: "13px 0", borderRadius: 10, border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
const INPUT = { width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, fontFamily: "inherit", marginBottom: 10 };

export default function OrderAccept() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(false);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(null);
  const [otpGate, setOtpGate] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0);

  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);

  const otpRequestCode = async (tok) => {
    setOtpError(null);
    try {
      const r = await fetch("/api/otp-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "order", token: tok }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.sent) { setOtpGate(g => ({ ...(g || { token: tok }), maskedEmail: d.maskedEmail || (g && g.maskedEmail) })); setOtpResendIn(30); }
      else if (!r.ok) setOtpError(d.error || "The code could not be sent. Please try again shortly.");
    } catch (_) { setOtpError("The code could not be sent. Please check your connection and try again."); }
  };

  const load = async (tok) => {
    try {
      const r = await fetch("/api/order-form", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "data", token: tok }) });
      const d = await r.json().catch(() => null);
      if (r.status === 401 && d && d.otpRequired) { setOtpGate(g => g || { token: tok, maskedEmail: d.maskedEmail || null }); otpRequestCode(tok); setLoading(false); return; }
      if (!r.ok || !d || !d.ok) { setError((d && d.error) || "This order form could not be loaded."); setLoading(false); return; }
      if (d.status !== "pending") setDone(d);
      setData({ ...d, token: tok });
      if (d.recipientName && !name) setName(d.recipientName);
      setLoading(false);
    } catch (_) { setError("This order form could not be loaded. Please check your connection and try again."); setLoading(false); }
  };

  const otpVerifyCode = async () => {
    if (!otpGate || otpBusy) return;
    setOtpBusy(true); setOtpError(null);
    try {
      const r = await fetch("/api/otp-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "order", token: otpGate.token, code: otpCode.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) { const tok = otpGate.token; setOtpGate(null); setOtpCode(""); setLoading(true); load(tok); }
      else { setOtpError(d.error || "That code did not verify. Please try again."); if (d.requestNew) setOtpCode(""); }
    } catch (_) { setOtpError("Verification failed. Please check your connection and try again."); }
    finally { setOtpBusy(false); }
  };

  const accept = async () => {
    if (accepting || !data) return;
    setAccepting(true); setError(null);
    try {
      const r = await fetch("/api/order-form", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "accept", token: data.token, name: name.trim(), title: title.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { setError(d.error || "Acceptance could not be recorded. Please try again."); setAccepting(false); return; }
      setDone({ status: "accepted", acceptedAt: d.acceptedAt, pdfUrl: d.pdfUrl, acceptedByName: name.trim() });
      setAccepting(false);
    } catch (_) { setError("Acceptance could not be recorded. Please check your connection and try again."); setAccepting(false); }
  };

  useEffect(() => {
    const tok = new URLSearchParams(window.location.search).get("token") || "";
    if (!/^[a-f0-9]{32,128}$/i.test(tok)) { setError("This link is not valid."); setLoading(false); return; }
    load(tok);
  }, []);

  if (loading) return <div style={PAGE}><div style={CARD}><div style={{ fontSize: 14, color: "#6b7280" }}>Loading…</div></div></div>;

  if (otpGate) {
    return (
      <div style={PAGE}>
        <div style={{ ...CARD, width: 480, alignSelf: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Verify it's you</div>
          <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.65, marginBottom: 20 }}>
            A six-digit code has been emailed to {otpGate.maskedEmail ? <b>{otpGate.maskedEmail}</b> : "the address this order form was sent to"}. Enter it below to view the CompliantID Order Form awaiting your acceptance.
          </div>
          <input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={e => { if (e.key === "Enter" && otpCode.length === 6) otpVerifyCode(); }} inputMode="numeric" autoComplete="one-time-code" placeholder="······"
            style={{ width: "100%", boxSizing: "border-box", fontSize: 28, letterSpacing: 12, textAlign: "center", padding: "12px 0", border: "1px solid #d1d5db", borderRadius: 10, marginBottom: 12, fontFamily: "inherit" }} />
          {otpError && <div style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>{otpError}</div>}
          <button onClick={otpVerifyCode} disabled={otpBusy || otpCode.length !== 6} style={{ ...BTN, background: otpCode.length === 6 ? "#0f766e" : "#9ca3af", color: "#fff", cursor: otpCode.length === 6 ? "pointer" : "not-allowed" }}>
            {otpBusy ? "Verifying…" : "Verify and continue"}
          </button>
          <div style={{ marginTop: 14, fontSize: 13, color: "#6b7280", textAlign: "center" }}>
            {otpResendIn > 0 ? `You can request a new code in ${otpResendIn}s` : <button onClick={() => otpRequestCode(otpGate.token)} style={{ background: "none", border: "none", color: "#0f766e", cursor: "pointer", fontSize: 13, fontFamily: "inherit", textDecoration: "underline" }}>Send a new code</button>}
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) return <div style={PAGE}><div style={CARD}><div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Order Form</div><div style={{ fontSize: 14, color: "#b91c1c" }}>{error}</div></div></div>;

  if (done) {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>{done.status === "accepted" ? "Order Form accepted" : `This order form is ${done.status}`}</div>
          {done.status === "accepted" && (
            <div style={{ fontSize: 14.5, color: "#374151", lineHeight: 1.7 }}>
              Thank you{done.acceptedByName ? `, ${done.acceptedByName}` : ""}. The Order Form for <b>{data && data.firmName}</b> was accepted at {String(done.acceptedAt || "").replace("T", " ").slice(0, 16)} UTC and is recorded on your firm's CompliantID account. Your subscription, allowance and billing settings now match it.
              {done.pdfUrl && <div style={{ marginTop: 16 }}><a href={done.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#0f766e", fontWeight: 700 }}>Download the accepted Order Form (PDF)</a></div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <div style={CARD}>
        <div style={{ fontSize: 12, color: "#0f766e", fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", marginBottom: 6 }}>CompliantID Ltd</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Order Form</div>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>For {data.firmName}. Incorporates the <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: "#0f766e" }}>Platform Terms and Conditions (v{data.termsVersion})</a> and the <a href="/dpa.html" target="_blank" rel="noopener noreferrer" style={{ color: "#0f766e" }}>Data Processing Agreement (v{data.dpaVersion})</a>.</div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, marginBottom: 22 }}>
          <tbody>
            {(data.lines || []).map(([k, v], i) => (
              <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                <td style={{ padding: "9px 10px 9px 0", color: "#0f766e", fontWeight: 700, width: 170, verticalAlign: "top" }}>{k}</td>
                <td style={{ padding: "9px 0", color: "#1f2937", lineHeight: 1.55 }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {data.pdfUrl && <div style={{ fontSize: 13, marginBottom: 18 }}><a href={data.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#0f766e" }}>Open as PDF</a></div>}

        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Signatory</div>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" style={INPUT} />
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title or role (e.g. Director, MLRO)" style={INPUT} />

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "10px 0 18px", cursor: "pointer", padding: "12px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10 }}>
          <input type="checkbox" checked={tick} onChange={e => setTick(e.target.checked)} style={{ marginTop: 3, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.65 }}>{data.statement}</span>
        </label>

        {error && <div style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>{error}</div>}
        <button onClick={accept} disabled={!tick || !name.trim() || accepting} style={{ ...BTN, background: tick && name.trim() ? "#0f766e" : "#9ca3af", color: "#fff", cursor: tick && name.trim() ? "pointer" : "not-allowed" }}>
          {accepting ? "Recording…" : "Accept the Order Form"}
        </button>
        <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 14, lineHeight: 1.6 }}>CompliantID is a technology platform and does not provide legal or compliance advice. Your acceptance is recorded with your name, verified email, the time, and network details, and has the same effect as a signature.</div>
      </div>
    </div>
  );
}
